import { useState } from 'react';
import { Maximize2, X } from 'lucide-react';
import { useEmailTheme, type EmailThemeId } from './emailTheme';

interface EmailPreviewProps {
  blocks: any[];
  expandable?: boolean;
  /** Produto dono do template; sem ele, vale o produto selecionado. */
  productId?: number | null;
}

function decodeHtmlEntities(str: string): string {
  return str
    .replace(/&amp;/g, '&')
    .replace(/&#x2F;/gi, '/')
    .replace(/&#47;/g, '/')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
}

/** Decodifica entidades HTML até estabilizar (camadas múltiplas de encoding). */
function decodeAll(str: string): string {
  let decoded = str;
  let prev = '';
  while (decoded !== prev) {
    prev = decoded;
    decoded = decodeHtmlEntities(decoded);
  }
  return decoded;
}

function sanitizeUrl(url: string): string {
  if (!url) {
    return '#';
  }
  return decodeAll(url).trim();
}

function ensureProtocol(url: string): string {
  const clean = sanitizeUrl(url);
  if (clean === '#') {
    return clean;
  }
  if (/^https?:\/\//i.test(clean)) {
    return clean;
  }
  if (/^mailto:/i.test(clean)) {
    return clean;
  }
  return `https://${clean}`;
}

function looksLikeHtml(s: string): boolean {
  return /<[a-z][\s\S]*>/i.test(s || '');
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** Valor que vai dentro de atributo entre aspas (igual ao escapeAttr do backend). */
function escapeAttr(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** Texto do bloco já decodificado e escapado para o corpo do HTML. */
function plainText(s: string | undefined): string {
  return escapeHtml(decodeAll(s || ''));
}

/** Só http(s), mailto ou caminho relativo; o resto vira '#'. Já sai escapado para atributo. */
function attrUrl(url: string | undefined): string {
  const clean = sanitizeUrl(url || '');
  if (/^(https?:|mailto:)/i.test(clean) || (clean.startsWith('/') && !clean.startsWith('//'))) {
    return escapeAttr(clean);
  }
  return '#';
}

/** Cor do bloco escapada para o atributo style. */
function attrColor(color: string | undefined, fallback: string): string {
  return escapeAttr(color || fallback);
}

function highlightVariables(html: string, theme: EmailThemeId = 'default'): string {
  const style =
    theme === 'docugen'
      ? 'background:#48bdc8;color:#17365d;font-weight:700;padding:0 2px;'
      : 'color:#2563eb;font-weight:600;';
  return html.replace(/\{\{(\w+)\}\}/g, `<span style="${style}">{{$1}}</span>`);
}

function autoLink(html: string, color = '#2563eb'): string {
  return html.replace(
    /(?<!href=["'])(?<!src=["'])(https?:\/\/[^\s<>"']+)/g,
    `<a href="$1" style="color:${color};text-decoration:underline;">$1</a>`
  );
}

function formatTextContent(
  text: string,
  theme: EmailThemeId = 'default',
  linkColor = '#2563eb'
): string {
  let result: string;
  if (looksLikeHtml(text)) {
    result = text;
  } else {
    result = escapeHtml(text).replace(/\n/g, '<br/>');
  }
  result = highlightVariables(result, theme);
  result = autoLink(result, linkColor);
  return result;
}

// ─── Terminal do bloco janela (igual ao backend) ─────────

interface TerminalLine {
  label: string;
  value: string | null;
}

export function parseTerminal(raw: string | undefined): TerminalLine[] {
  if (!raw) {
    return [];
  }
  return sanitizeUrl(raw)
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const idx = line.indexOf(':');
      if (idx <= 0) {
        return { label: line, value: null };
      }
      return { label: line.slice(0, idx).trim(), value: line.slice(idx + 1).trim() };
    });
}

export function renderTerminalLines(lines: TerminalLine[], accent: string): string {
  const width = Math.max(...lines.map((l) => (l.value === null ? 0 : [...l.label].length))) + 3;
  const prompt = `<span style="color:${accent};">&gt;</span>`;
  return lines
    .map((l) => {
      if (l.value === null) {
        return `${prompt} ${escapeHtml(l.label)}`;
      }
      const dots = '.'.repeat(Math.max(1, width - [...l.label].length - 2));
      return `${prompt} ${escapeHtml(l.label)} ${dots} <span style="color:${accent};">${escapeHtml(l.value)}</span>`;
    })
    .join('<br>');
}

// ─── Tema padrão ─────────────────────────────────────────

function renderBlockDefault(block: any): string {
  switch (block.type) {
    case 'header': {
      const logo = block.logoUrl
        ? `<img src="${attrUrl(block.logoUrl)}" alt="Logo" style="max-height:48px;margin-bottom:8px;" />`
        : '';
      return `
        <div style="background:#1e40af;padding:32px 24px;text-align:center;border-radius:8px 8px 0 0;">
          ${logo}
          <h1 style="color:#ffffff;font-size:24px;font-weight:700;margin:0;font-family:sans-serif;">
            ${plainText(block.title) || 'Título do E-mail'}
          </h1>
        </div>
      `;
    }

    case 'text': {
      const content = block.content ? formatTextContent(block.content) : '';
      return `
        <div style="padding:24px;font-family:sans-serif;font-size:14px;line-height:1.7;color:#374151;">
          ${content || '<em style="color:#9ca3af;">Sem conteúdo</em>'}
        </div>
      `;
    }

    case 'subtitle': {
      const color = attrColor(block.color, '#111827');
      const text = block.text ? highlightVariables(escapeHtml(block.text)) : '';
      return `
        <div style="padding:8px 24px 0 24px;">
          <h2 style="font-family:sans-serif;font-size:20px;font-weight:700;color:${color};margin:0;line-height:1.3;">
            ${text || '<em style="color:#9ca3af;font-weight:normal;">Subtítulo vazio</em>'}
          </h2>
        </div>
      `;
    }

    case 'image': {
      if (!block.url) {
        return imagePlaceholder();
      }
      return `
        <div style="padding:16px 24px;text-align:center;">
          <img src="${attrUrl(block.url)}" alt="${escapeAttr(decodeAll(block.alt || ''))}" style="max-width:100%;height:auto;border-radius:6px;" />
        </div>
      `;
    }

    case 'cta': {
      const color = attrColor(block.color, '#2563eb');
      return `
        <div style="padding:20px 24px;text-align:center;">
          <a href="${attrUrl(ensureProtocol(block.url))}" style="display:inline-block;background:${color};color:#ffffff;padding:12px 32px;border-radius:8px;font-family:sans-serif;font-size:14px;font-weight:600;text-decoration:none;">
            ${plainText(block.text) || 'Clique aqui'}
          </a>
        </div>
      `;
    }

    case 'divider': {
      const color = attrColor(block.color, '#e5e7eb');
      const thickness = Number(block.thickness) || 1;
      return `
        <div style="padding:8px 24px;">
          <hr style="border:none;border-top:${thickness}px solid ${color};margin:0;" />
        </div>
      `;
    }

    case 'footer': {
      const content = block.content
        ? formatTextContent(block.content).replace(/color:#2563eb/g, 'color:#6b7280')
        : '';
      return `
        <div style="background:#f9fafb;padding:20px 24px;border-radius:0 0 8px 8px;text-align:center;font-family:sans-serif;font-size:12px;color:#6b7280;line-height:1.6;border-top:1px solid #e5e7eb;">
          ${content || '<em>Rodapé do e-mail</em>'}
        </div>
      `;
    }

    case 'window': {
      const lines = parseTerminal(block.terminal);
      const content = block.content ? formatTextContent(block.content) : '';
      const terminal = lines.length
        ? `<div style="margin:8px 16px 16px;background:#0f172a;border-radius:6px;padding:14px 16px;font-family:'Courier New',monospace;font-size:13px;line-height:22px;color:#e2e8f0;">${renderTerminalLines(lines, '#38bdf8')}</div>`
        : '';
      return `
        <div style="padding:16px 24px;">
          <div style="border:1px solid #e2e8f0;border-radius:8px;overflow:hidden;">
            <div style="background:#f1f5f9;padding:8px 14px;font-family:sans-serif;font-size:12px;font-weight:700;letter-spacing:1px;color:#475569;">${escapeHtml(block.title || 'JANELA')}</div>
            ${content ? `<div style="padding:16px 16px 4px;font-family:sans-serif;font-size:14px;line-height:1.7;color:#374151;">${content}</div>` : ''}
            ${terminal}
          </div>
        </div>
      `;
    }

    default:
      return '';
  }
}

function imagePlaceholder(): string {
  return `
    <div style="padding:16px 24px;">
      <div style="background:#f3f4f6;border:2px dashed #d1d5db;border-radius:8px;padding:32px;text-align:center;color:#9ca3af;font-family:sans-serif;font-size:13px;">
        Imagem não configurada
      </div>
    </div>
  `;
}

// ─── Tema DocuGen (igual ao backend) ─────────────────────

const DG = {
  navy: '#17365d',
  navyDeep: '#0c1f3a',
  sky: '#1d4a85',
  cyan: '#48bdc8',
  cream: '#fffdf2',
  paper: '#eef0f3',
  footerText: '#c9ccd1',
  font: "'Space Mono','Courier New',monospace",
};

export const DOCUGEN_TOPO_PATH = '/email/docugen-topo.png';

function renderBlockDocugen(block: any): string {
  switch (block.type) {
    case 'header': {
      const title = block.title ? highlightVariables(plainText(block.title), 'docugen') : '';
      if (block.logoUrl) {
        return `<tr><td style="padding:0;background:${DG.sky};"><img src="${attrUrl(block.logoUrl)}" width="600" alt="${escapeAttr(decodeAll(block.title || ''))}" style="display:block;width:100%;max-width:600px;height:auto;border:0;" /></td></tr>`;
      }
      const headline = title.trim()
        ? `<tr><td style="background:${DG.sky};padding:0 32px 28px;text-align:center;font-family:${DG.font};font-size:22px;line-height:30px;font-weight:bold;color:#ffffff;">${title}</td></tr>`
        : '';
      return `<tr><td style="padding:0;background:${DG.sky};"><img src="${DOCUGEN_TOPO_PATH}" width="600" alt="DocuGen" style="display:block;width:100%;max-width:600px;height:auto;border:0;" /></td></tr>${headline}`;
    }

    case 'text': {
      const content = block.content ? formatTextContent(block.content, 'docugen', DG.navy) : '';
      return `<tr><td class="dg-text" style="padding:14px 32px;font-family:${DG.font};font-size:15px;line-height:24px;color:${DG.navy};">${content || '<em style="color:#9ca3af;">Sem conteúdo</em>'}</td></tr>`;
    }

    case 'subtitle': {
      const text = block.text ? highlightVariables(escapeHtml(block.text), 'docugen') : '';
      return `<tr><td style="padding:24px 32px 0;"><h2 style="margin:0;font-family:${DG.font};font-size:18px;line-height:26px;font-weight:bold;color:${DG.navy};">${text || '<em style="color:#9ca3af;font-weight:normal;">Subtítulo vazio</em>'}</h2></td></tr>`;
    }

    case 'image':
      if (!block.url) {
        return `<tr><td>${imagePlaceholder()}</td></tr>`;
      }
      return `<tr><td style="padding:16px 32px;"><img src="${attrUrl(block.url)}" alt="${escapeAttr(decodeAll(block.alt || ''))}" style="display:block;width:100%;max-width:536px;height:auto;border:2px solid ${DG.navy};" /></td></tr>`;

    case 'cta':
      return `<tr><td align="center" style="padding:24px 32px 32px;">
        <table role="presentation" cellpadding="0" cellspacing="0" border="0"><tr>
          <td style="background:${DG.navy};padding:0 4px 4px 0;">
            <a href="${attrUrl(ensureProtocol(block.url))}" target="_blank" rel="noopener noreferrer" style="display:inline-block;background:${DG.cyan};border:2px solid ${DG.navy};padding:14px 28px;font-family:${DG.font};font-size:15px;font-weight:bold;letter-spacing:2px;text-transform:uppercase;color:${DG.navy};text-decoration:none;">${plainText(block.text) || 'Clique aqui'}</a>
          </td>
        </tr></table>
      </td></tr>`;

    case 'divider':
      return `<tr><td style="padding:12px 32px;"><hr style="border:none;border-top:2px dashed ${DG.navy};margin:0;" /></td></tr>`;

    case 'footer': {
      const content = block.content
        ? formatTextContent(block.content, 'docugen', DG.footerText)
        : '';
      return `<tr><td align="center" class="dg-footer" style="background:${DG.navy};padding:20px 24px;font-family:${DG.font};font-size:12px;line-height:20px;color:${DG.footerText};">${content || '<em>Rodapé do e-mail</em>'}</td></tr>`;
    }

    case 'window': {
      const lines = parseTerminal(block.terminal);
      const content = block.content ? formatTextContent(block.content, 'docugen', DG.navy) : '';
      const body = content
        ? `<tr><td class="dg-text" style="padding:22px 22px 6px;font-family:${DG.font};font-size:15px;line-height:24px;color:${DG.navy};">${content}</td></tr>`
        : '';
      const terminal = lines.length
        ? `<tr><td style="padding:${body ? '0' : '22px'} 22px 22px;"><table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:${DG.navyDeep};"><tr><td style="padding:16px 18px;font-family:${DG.font};font-size:14px;line-height:24px;color:#ffffff;">${renderTerminalLines(lines, DG.cyan)}</td></tr></table></td></tr>`
        : '';
      const square = `<span style="display:inline-block;width:12px;height:12px;background:${DG.paper};margin-left:6px;"></span>`;
      return `<tr><td style="padding:32px 32px 8px;">
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="border:2px solid ${DG.navy};background:${DG.paper};">
          <tr><td style="background:${DG.navy};padding:10px 14px;">
            <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr>
              <td style="font-family:${DG.font};font-size:13px;font-weight:bold;letter-spacing:2px;color:#ffffff;">${escapeHtml(block.title || 'JANELA')}</td>
              <td align="right" style="font-size:0;line-height:0;">${square}${square}${square}</td>
            </tr></table>
          </td></tr>
          ${body}
          ${terminal}
        </table>
      </td></tr>`;
    }

    default:
      return '';
  }
}

export function renderEmailHtml(blocks: any[], theme: EmailThemeId = 'default'): string {
  if (theme === 'docugen') {
    const rows = blocks.map(renderBlockDocugen).join('');
    return `
      <link href="https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&display=swap" rel="stylesheet">
      <style>
        .dg-preview .dg-text a { color:${DG.navy}; text-decoration:underline; }
        .dg-preview .dg-footer a { color:${DG.footerText}; text-decoration:none; }
        .dg-preview .dg-text p { margin:0 0 16px 0; }
        .dg-preview .dg-footer p { margin:0; }
      </style>
      <div class="dg-preview" style="background:${DG.navy};padding:24px 0;">
        <table role="presentation" width="600" cellpadding="0" cellspacing="0" border="0" style="width:100%;max-width:600px;margin:0 auto;background:${DG.cream};border-collapse:collapse;">
          ${rows || `<tr><td style="padding:48px 24px;text-align:center;color:#9ca3af;font-family:${DG.font};font-size:14px;">Nenhum bloco adicionado</td></tr>`}
        </table>
      </div>
    `;
  }
  const html = blocks.map(renderBlockDefault).join('');
  return `
    <div style="max-width:600px;margin:0 auto;background:#ffffff;border-radius:8px;overflow:hidden;box-shadow:0 1px 3px rgba(0,0,0,0.1);">
      ${html || '<div style="padding:48px 24px;text-align:center;color:#9ca3af;font-family:sans-serif;font-size:14px;">Nenhum bloco adicionado</div>'}
    </div>
  `;
}

export function EmailPreviewModal({
  blocks,
  open,
  onClose,
  productId,
}: {
  blocks: any[];
  open: boolean;
  onClose: () => void;
  productId?: number | null;
}) {
  const theme = useEmailTheme(productId);
  if (!open) {
    return null;
  }
  const fullHtml = renderEmailHtml(blocks, theme);
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-3xl max-h-[92vh] bg-surface rounded-xl shadow-2xl border border-border flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-surface flex-shrink-0">
          <p className="text-sm font-semibold text-ink-primary">Pré-visualização do e-mail</p>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-ink-tertiary hover:text-ink-primary hover:bg-subtle transition-colors"
            title="Fechar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto bg-gray-100 p-6">
          {/* HTML is built from sanitized blocks (see backend blocks-sanitizer) */}
          <div
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: fullHtml }}
          />
        </div>
        <div className="px-4 py-2.5 border-t border-border text-xs text-ink-tertiary text-center flex-shrink-0">
          Esta é a aparência aproximada do e-mail no inbox do destinatário.
        </div>
      </div>
    </div>
  );
}

export function EmailPreview({ blocks, expandable = false, productId }: EmailPreviewProps) {
  const [open, setOpen] = useState(false);
  const theme = useEmailTheme(productId);
  const fullHtml = renderEmailHtml(blocks, theme);

  return (
    <div className="bg-gray-100 rounded-xl p-4 overflow-y-auto">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
          Preview do E-mail
        </p>
        {expandable && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-ink-secondary hover:text-ink-primary hover:bg-subtle rounded-md transition-colors"
            title="Expandir pré-visualização"
          >
            <Maximize2 className="w-3.5 h-3.5" />
            Expandir
          </button>
        )}
      </div>
      {/* HTML is built from sanitized blocks (see backend blocks-sanitizer) */}
      <div
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: fullHtml }}
      />
      <EmailPreviewModal
        blocks={blocks}
        open={open}
        onClose={() => setOpen(false)}
        productId={productId}
      />
    </div>
  );
}

export default EmailPreview;
